import { useStore } from '@/store/useStore';
import { useEffect, useState } from 'react';
import {
  BarChart3, Calendar, Briefcase, Users, Building2, Monitor, ChevronDown, ChevronUp,
  Settings, Sun, Moon, User, LogOut, ClipboardList, FileSpreadsheet, Shield, Timer,
  Menu, X, ClipboardCheck, ListChecks, Wallet, Bell, Cake,
} from 'lucide-react';
import { useIsMobile } from '@/hooks/useIsMobile';
// Banknote — иконка вкладки «Финансы» (временно скрыта); вернуть в импорт при возврате блока.
import { cn } from '@/utils/cn';
import { CityIcon } from './icons/CityIcon';
import type { Page } from '@/store/useStore';
import { usePerms } from '@/auth/usePerms';
import { canManageBranches } from '@/auth/permissions';
import { getNotifications, markNotificationRead, type NotificationItem } from '@/auth/adminClient';

// Сдвиги часовых поясов для списка «Другие часовые пояса» (минуты от UTC).
const TZ_OFFSETS = [
  -720, -660, -600, -570, -540, -480, -420, -360, -300, -240, -180, -150, -120, -60,
  0, 60, 120, 180, 210, 240, 270, 300, 330, 345, 360, 390, 420, 480, 510, 525, 540,
  570, 600, 630, 660, 720, 765, 780, 840,
];

function offsetLabel(min: number): string {
  const s = min < 0 ? '-' : '+';
  const a = Math.abs(min);
  return `UTC${s}${String(Math.floor(a / 60)).padStart(2, '0')}:${String(a % 60).padStart(2, '0')}`;
}

/** Время «на стене» при заданном сдвиге от UTC. */
function timeAtOffset(nowMs: number, offsetMin: number): string {
  const d = new Date(nowMs + offsetMin * 60_000);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

function localTime(nowMs: number): string {
  const d = new Date(nowMs);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function TopNav() {
  const { page, setPage, branches, filterBranchId, setFilterBranch, settings, patchSettings, bitrixUser, clockTz, setClockTz, setPersonnelTab } = useStore();
  const currentUser = useStore((s) => s.currentUser);
  // Фоновая догрузка всех сделок (после быстрого показа текущего месяца) — ненавязчивый индикатор.
  const dealsLoadingMore = useStore((s) => s.dealsLoadingMore);
  // «Доступы»/управление ролями — только у супер-админа (отдельный аккаунт), не у обычных ролей.
  const isSuperAdmin = currentUser?.access_role === 'superadmin';
  // «Филиалы» — только руководитель сети и супер-админ (управляющего филиала сюда не пускаем).
  const canBranches = canManageBranches(currentUser);
  // «Мои игры» — только тем, кто привязан к карточке сотрудника: у остальных (напр. чистый
  // менеджер без карточки) список был бы всегда пустым.
  const hasEmployeeCard = !!currentUser?.employee_id;
  const logout = useStore((s) => s.logout);
  const p = usePerms();
  const [branchOpen, setBranchOpen] = useState(false);
  const [clockOpen, setClockOpen] = useState(false);
  const [moreTz, setMoreTz] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [confirmExit, setConfirmExit] = useState(false); // двухшаговый выход (надёжно на телефоне/во встройке, где confirm() блокируется)
  // На телефоне ряд вкладок съедал четверть экрана — прячем его под «бургер» рядом с логотипом.
  const isMobile = useIsMobile();
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => { if (!isMobile) setNavOpen(false); }, [isMobile]);
  // Переход по вкладке на телефоне сразу схлопывает меню.
  const go = (p: Page) => { setPage(p); setNavOpen(false); };

  const selectedBranch = filterBranchId !== 'all' ? branches.find((b) => b.id === filterBranchId) : null;
  // Если в карточке человека отмечены города — в переключателе показываем только их
  // (данные по остальным сервер всё равно не отдаст).
  const myCities = currentUser?.city_ids ?? [];
  const visibleBranches = myCities.length > 0 ? branches.filter((b) => myCities.includes(b.id)) : branches;

  // Тикаем раз в 15 секунд, чтобы часы шли.
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 15_000); return () => clearInterval(id); }, []);
  const localOff = -new Date().getTimezoneOffset();

  // Если выбранный для часов филиал перестал быть выбран — вернуться на системное время.
  useEffect(() => { if (clockTz.kind === 'branch' && !selectedBranch) setClockTz({ kind: 'system' }); }, [clockTz.kind, selectedBranch]);

  // Текст на кнопке-часах в зависимости от выбранного пояса.
  let clockText: string;
  if (clockTz.kind === 'system') clockText = localTime(now);
  else if (clockTz.kind === 'branch') clockText = selectedBranch ? timeAtOffset(now, selectedBranch.tz_offset_minutes) : localTime(now);
  else clockText = timeAtOffset(now, clockTz.min);

  const isDark = settings.theme === 'dark';
  const userName = currentUser?.full_name ?? bitrixUser ?? 'Пользователь';
  const roleName = currentUser?.access_role_name ?? currentUser?.worker_type_name ?? 'Роль не задана';

  // Пункты разделов — общий список для десктопной строки (рядом с лого) и мобильного
  // выпадающего меню (под гамбургером), чтобы не дублировать JSX в двух местах.
  const navLinks = (
    <>
      {p.pages.dashboard && (
        <button onClick={() => go('dashboard')} className={cn('tab-link', page === 'dashboard' && 'tab-link-active')}>
          <BarChart3 className="w-4 h-4" /> Дашборд
        </button>
      )}
      {p.pages.calendar && (
        <button onClick={() => go('calendar')} className={cn('tab-link', page === 'calendar' && 'tab-link-active')}>
          <Calendar className="w-4 h-4" /> Календарь
        </button>
      )}

      {/* Продажи — выпадающее меню (Формы / Игры / Отчёты / Заполняемость);
          «Отчёты» и «Заполняемость» — по одному праву reports */}
      {p.pages.sales && (
        <NavDropdown
          label="Продажи" icon={Briefcase}
          active={page === 'crm' || page === 'forms' || page === 'reports' || page === 'fill_rate'}
          items={[
            { key: 'forms', label: 'Формы', icon: ClipboardList },
            { key: 'crm', label: 'Игры', icon: FileSpreadsheet },
            ...(p.pages.reports ? [{ key: 'reports' as Page, label: 'Отчёты', icon: BarChart3 }] : []),
            ...(p.pages.reports ? [{ key: 'fill_rate' as Page, label: 'Заполняемость', icon: ClipboardCheck }] : []),
          ]}
          page={page} onPick={go}
        />
      )}

      {/* Персонал — обычная кнопка: сразу «Сотрудники» (внутри уже есть вкладки Сотрудники/Команды).
          Кассиру раздел открыт по узкому праву «Загрузка»: внутри он увидит только эту вкладку. */}
      {(p.pages.personnel || p.pages.workload) && (
        <button onClick={() => { setPersonnelTab('employees'); go('personnel'); }} className={cn('tab-link', page === 'personnel' && 'tab-link-active')}>
          <Users className="w-4 h-4" /> Персонал
        </button>
      )}

      {/* Ставки на расчёт зарплаты — защищённый экран, отдельное право pay_rates
          (финансист/руководитель/супер-админ, настраивается в «Роли и права»). */}
      {p.pages.pay_rates && (
        <button onClick={() => go('pay_rates')} className={cn('tab-link', page === 'pay_rates' && 'tab-link-active')}>
          <Wallet className="w-4 h-4" /> Ставки
        </button>
      )}

      {/* «Мои игры» — личный список отработанных игр. Виден всем, кто привязан к карточке
          сотрудника (ведущему/диджею и т.д.); отдельного права не требует. */}
      {hasEmployeeCard && (
        <button onClick={() => go('my_games')} className={cn('tab-link', page === 'my_games' && 'tab-link-active')}>
          <ListChecks className="w-4 h-4" /> Мои игры
        </button>
      )}

      {/* Филиалы — подключение города из Битрикса и рабочей группы Telegram: только руководитель/супер-админ. */}
      {canBranches && (
        <button onClick={() => go('branches')} className={cn('tab-link', page === 'branches' && 'tab-link-active')}>
          <Building2 className="w-4 h-4" /> Филиалы
        </button>
      )}
    </>
  );

  return (
    <header className="border-b border-line bg-surface-1 flex-shrink-0">
      {/* На телефоне отступы и промежутки уже: освободившееся место уходит названию филиала,
          иначе длинные города («Петропавловск», «Все филиалы») выдавливали шестерёнку за экран. */}
      <div className="h-14 px-2 sm:px-5 flex items-center gap-1.5 sm:gap-3">
        {isMobile && (
          <button className="icon-btn flex-shrink-0" onClick={() => setNavOpen((v) => !v)}
            title="Меню разделов" aria-label="Меню разделов" aria-expanded={navOpen}>
            {navOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        )}
        <button className="flex items-center gap-1 select-none ml-0 sm:ml-6 flex-shrink-0" onClick={() => setPage('dashboard')}>
          <span className="text-xl sm:text-2xl font-black bg-gradient-to-br from-accent to-warm-coral bg-clip-text text-transparent">
            +ERP
          </span>
        </button>

        {/* Разделы — на десктопе сразу справа от лого, в одну строку с шапкой.
            На телефоне остаются отдельной строкой ниже, под гамбургер-меню (см. <nav> внизу). */}
        <div className="hidden sm:flex items-center gap-1.5 flex-wrap">
          {navLinks}
        </div>

        {/* Пока в фоне доливаются все игры (стартовали с текущего месяца) — тихая подсказка.
            На телефоне прячем, чтобы не отъедать место у филиала/часов. */}
        {dealsLoadingMore && (
          <span className="hidden sm:flex items-center gap-1.5 text-[11px] text-ink-3 select-none" title="Догружаются остальные игры">
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
            данные догружаются…
          </span>
        )}

        <div className="flex-1" />

        {/* Branch dropdown — только если роль видит несколько городов */}
        {p.citySwitcher && (
        // min-w-0 — чтобы на узком экране блок МОГ сжаться: без него flex держит кнопку по
        // ширине текста, шапка вылезает за экран и шестерёнка уезжает за правый край
        // («Все филиалы»/«Петропавловск» — 419 px при экране 390). Сжимается только
        // название филиала: часы и шестерёнка нужны всегда, их не режем.
        <div className="relative min-w-0 shrink">
          <button
            className="flex items-center gap-2 h-9 px-2 sm:px-3 rounded-lg bg-accent-soft/60 text-accent hover:bg-accent-soft transition-colors font-medium text-sm w-full min-w-0 max-w-[140px] sm:max-w-none"
            onClick={() => setBranchOpen((v) => !v)}
          >
            <CityIcon className="w-4 h-4 flex-shrink-0" style={selectedBranch ? { color: selectedBranch.color } : undefined} />
            <span className="truncate">{selectedBranch?.name ?? 'Все филиалы'}</span>
            <ChevronDown className="w-3.5 h-3.5 opacity-70 flex-shrink-0" />
          </button>
          {branchOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setBranchOpen(false)} />
              <div className="absolute right-0 top-11 z-40 menu min-w-0 w-fit max-h-[420px] overflow-y-auto">
                <button
                  className={cn('menu-item w-full', filterBranchId === 'all' && 'text-accent font-semibold bg-accent-soft/40')}
                  onClick={() => { setFilterBranch('all'); setClockTz({ kind: 'system' }); setBranchOpen(false); }}
                >
                  <CityIcon className="w-4 h-4" /> Все филиалы
                </button>
                <div className="h-px bg-line my-1" />
                {visibleBranches.map((b) => (
                  <button
                    key={b.id}
                    className={cn('menu-item w-full', filterBranchId === b.id && 'text-accent font-semibold bg-accent-soft/40')}
                    onClick={() => { setFilterBranch(b.id); setClockTz({ kind: 'branch' }); setBranchOpen(false); }}
                  >
                    <CityIcon className="w-4 h-4 flex-shrink-0" style={{ color: b.color }} />
                    <span className="truncate text-left">{b.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        )}

        {/* Часы + меню часовых поясов */}
        <div className="relative flex-shrink-0">
          <button
            className="flex items-center gap-1.5 h-9 px-2 sm:px-3 rounded-lg bg-accent-soft/50 text-accent hover:bg-accent-soft transition-colors font-medium text-sm tabular-nums"
            onClick={() => setClockOpen((v) => !v)}
          >
            {/* На совсем узких телефонах (<380 px) иконку часового пояса прячем — само время
                остаётся. Она стоит перед филиалом и первой отъедала место у названия города. */}
            <Monitor className="w-4 h-4 hidden min-[380px]:block" />
            {clockText}
          </button>
          {clockOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => { setClockOpen(false); setMoreTz(false); }} />
              <div className="absolute right-0 top-11 z-40 menu min-w-[260px] max-h-[460px] overflow-y-auto">
                {/* Системное время */}
                <button
                  className={cn('w-full flex items-start gap-2 px-3 py-2 rounded-lg hover:bg-surface-2 text-left',
                    clockTz.kind === 'system' && 'text-accent')}
                  onClick={() => { setClockTz({ kind: 'system' }); setClockOpen(false); setMoreTz(false); }}
                >
                  <Monitor className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className={cn('font-semibold text-sm', clockTz.kind === 'system' && 'text-accent')}>Системное время</span>
                      <span className="text-sm tabular-nums">{localTime(now)}</span>
                    </div>
                    <div className="text-[11px] text-ink-3">{offsetLabel(localOff)}</div>
                  </div>
                </button>

                {/* Выбранный филиал */}
                {selectedBranch && (
                  <button
                    className={cn('w-full flex items-start gap-2 px-3 py-2 rounded-lg hover:bg-surface-2 text-left',
                      clockTz.kind === 'branch' && 'text-accent')}
                    onClick={() => { setClockTz({ kind: 'branch' }); setClockOpen(false); setMoreTz(false); }}
                  >
                    <Building2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className={cn('font-semibold text-sm', clockTz.kind === 'branch' && 'text-accent')}>{selectedBranch.name}</span>
                        <span className="text-sm tabular-nums">{timeAtOffset(now, selectedBranch.tz_offset_minutes)}</span>
                      </div>
                      <div className="text-[11px] text-ink-3">{offsetLabel(selectedBranch.tz_offset_minutes)}</div>
                    </div>
                  </button>
                )}

                {/* Другие часовые пояса */}
                <button
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-surface-2 text-ink-2 text-sm"
                  onClick={() => setMoreTz((v) => !v)}
                >
                  <span>Другие часовые пояса</span>
                  {moreTz ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {moreTz && (
                  <div className="border-t border-line mt-1 pt-1">
                    {TZ_OFFSETS.map((min) => {
                      const active = clockTz.kind === 'offset' && clockTz.min === min;
                      return (
                        <button
                          key={min}
                          className={cn('w-full flex items-center justify-between px-3 py-1.5 rounded-lg hover:bg-surface-2 text-left',
                            active && 'bg-accent-soft/40 text-accent')}
                          onClick={() => { setClockTz({ kind: 'offset', min }); setClockOpen(false); setMoreTz(false); }}
                        >
                          <span className="font-semibold text-sm">{offsetLabel(min)}</span>
                          <span className="text-sm tabular-nums text-ink-2">{timeAtOffset(now, min)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <NotificationsBell />

        {/* Шестерёнка — меню пользователя. Отступ справа такой же, как у лого слева (ml-6). */}
        <div className="relative mr-0 sm:mr-6 flex-shrink-0">
          <button className="icon-btn" onClick={() => { setUserOpen((v) => !v); setConfirmExit(false); }} title="Профиль / выход">
            <Settings className="w-4 h-4" />
          </button>
          {userOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => { setUserOpen(false); setConfirmExit(false); }} />
              <div className="absolute right-0 top-11 z-40 menu w-[190px] min-w-0 px-1">
                {/* Имя + роль */}
                <div className="flex items-center gap-2 px-2 py-2.5">
                  <div className="relative flex-shrink-0">
                    <span className="w-8 h-8 rounded-full bg-surface-2 text-ink-3 grid place-items-center">
                      <User className="w-4 h-4" />
                    </span>
                    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-surface-1" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-[13px] truncate">{userName}</div>
                    <div className="text-[11px] text-ink-3 leading-tight truncate">{roleName}</div>
                  </div>
                </div>
                <div className="h-px bg-line my-1" />
                <button className="menu-item rounded-lg w-full" onClick={() => { setUserOpen(false); setPage('profile'); }}>
                  <User className="w-4 h-4" /> Профиль
                </button>
                {isSuperAdmin && (
                  <button className="menu-item rounded-lg w-full" onClick={() => { setUserOpen(false); setPage('access'); }}>
                    <Shield className="w-4 h-4" /> Доступы
                  </button>
                )}
                {/* Настройки предброни — только руководитель сети и супер-админ. */}
                {canBranches && (
                  <button className="menu-item rounded-lg w-full" onClick={() => { setUserOpen(false); setPage('prebooking'); }}>
                    <Timer className="w-4 h-4" /> Настройки предброни
                  </button>
                )}
                {confirmExit ? (
                  <button className="menu-item rounded-lg w-full text-status-refused hover:!bg-status-refused/10 font-semibold"
                    onClick={() => { setUserOpen(false); setConfirmExit(false); void logout(); }}>
                    <LogOut className="w-4 h-4" /> Точно выйти?
                  </button>
                ) : (
                  <button className="menu-item rounded-lg w-full" onClick={() => setConfirmExit(true)}>
                    <LogOut className="w-4 h-4" /> Выйти
                  </button>
                )}
                <div className="h-px bg-line my-1" />
                <button
                  className="menu-item rounded-lg justify-between w-full"
                  onClick={() => void patchSettings({ theme: isDark ? 'light' : 'dark' })}
                >
                  <span className="flex items-center gap-2"><Monitor className="w-4 h-4" /> Тема:</span>
                  {isDark ? <Moon className="w-4 h-4 text-accent" /> : <Sun className="w-4 h-4 text-accent" />}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Мобильный аналог — отдельной строкой под гамбургером; на десктопе теперь не
          рендерим совсем (там те же пункты уже в первой строке, см. navLinks выше). */}
      {isMobile && (
        <nav className={cn('px-3 pb-2 items-center gap-1.5 flex-wrap', navOpen ? 'flex' : 'hidden')}>
          {navLinks}
        </nav>
      )}
    </header>
  );
}

/**
 * Колокольчик уведомлений (чат 02.11.2026) — рядом с шестерёнкой. Два вида:
 *  - «заполните дату рождения» — синтетическое, сервер считает на лету по пустому
 *    employees.birth_date; клик ведёт в «Профиль», само пропадает только после заполнения;
 *  - «скоро день рождения у коллеги» — хранится на сервере, клик помечает прочитанным.
 * Виден только тем, у кого есть привязанная карточка сотрудника (employee_id) — остальным
 * (сервисные/без карточки) нотификации не считаются вообще.
 */
function NotificationsBell() {
  const currentUser = useStore((s) => s.currentUser);
  const setPage = useStore((s) => s.setPage);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  // Супер-админ видит колокольчик даже без своей карточки сотрудника — сервер в этом случае
  // отдаёт ему системный обзор ВСЕХ непрочитанных «скоро ДР» (не свою ленту — своей у него нет).
  const canSee = !!currentUser?.employee_id || currentUser?.access_role === 'superadmin';

  useEffect(() => {
    if (!canSee) return;
    const load = () => { getNotifications().then(setItems).catch(() => {}); };
    load();
    const t = setInterval(load, 5 * 60_000); // раз в 5 минут — не горячий путь
    return () => clearInterval(t);
  }, [canSee]);

  if (!canSee) return null;

  const onPick = (it: NotificationItem) => {
    if (it.kind === 'fill_birthday') { setOpen(false); setPage('profile'); return; }
    setItems((p) => p.filter((x) => x.id !== it.id));
    void markNotificationRead(it.id);
  };

  return (
    <div className="relative flex-shrink-0">
      <button className="icon-btn relative" onClick={() => setOpen((v) => !v)} title="Уведомления">
        <Bell className="w-4 h-4" />
        {items.length > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 rounded-full bg-status-refused text-white text-[9px] font-bold grid place-items-center">
            {items.length > 9 ? '9+' : items.length}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-11 z-40 menu w-[300px] px-1 py-1.5 max-h-[70vh] overflow-y-auto">
            <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-ink-3 font-bold">Уведомления</div>
            {items.length === 0 ? (
              <div className="px-2 py-5 text-sm text-ink-3 text-center">Пока пусто</div>
            ) : (
              items.map((it) => (
                <button key={it.id} onClick={() => onPick(it)}
                  className={cn('w-full text-left px-2.5 py-2.5 rounded-lg hover:bg-surface-2 text-sm flex items-start gap-2',
                    it.kind === 'fill_birthday' && 'text-amber-600 font-medium')}>
                  {it.kind === 'birthday_soon' ? <Cake className="w-4 h-4 flex-shrink-0 mt-0.5 text-accent" /> : <Bell className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                  <span className="flex-1 leading-snug">{it.text}</span>
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Вкладка-навигация с выпадающим под-меню (как «Продажи» в эталоне).
function NavDropdown({ label, icon: Icon, active, items, page, onPick }: {
  label: string; icon: typeof BarChart3; active: boolean;
  items: { key: Page; label: string; icon: typeof BarChart3 }[];
  page: Page; onPick: (p: Page) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} className={cn('tab-link', active && 'tab-link-active')}>
        <Icon className="w-4 h-4" /> {label}
        <ChevronDown className="w-3.5 h-3.5 opacity-70" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-11 z-40 menu min-w-[230px] p-1.5 space-y-0.5">
            {items.map((it) => (
              <button key={it.key} className={cn('w-full flex items-center gap-3 px-3 h-12 rounded-xl text-[15px] transition-colors',
                page === it.key ? 'text-accent font-semibold bg-accent-soft/50' : 'text-ink-2 hover:bg-surface-2')}
                onClick={() => { onPick(it.key); setOpen(false); }}>
                <it.icon className="w-5 h-5" /> {it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
